// client/src/components/indicadores/ClientesReport.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ USANDO API PADRONIZADA
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { useClientesReport } from '../../hooks/useReports';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import { Users } from 'lucide-react';
import ReportPageLayout from './ReportPageLayout';
import ReportTable from './ReportTable';

// ✅ DEFINIR FILTROS INICIAIS COMO CONSTANTE
const initialFilterState = {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    servicoId: '',
    profissionalId: '',
    unidadeId: '',
    recursoId: '',
    comAgendamento: 'sim',
};

const ClientesReport = () => {
    const { setAlert } = useAlert();
    const { user, scope } = useAuth();
    // ✅ ACHADO: relatorio fixo em BRL, sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();

    const [appliedFilters, setAppliedFilters] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        servicos: [],
        profissionais: [],
        unidades: [],
        recursos: [],
    });
    const [loadingFilters, setLoadingFilters] = useState(false);

    // 🔥 USANDO NOVO HOOK - useClientesReport
    const { 
        data: reportData = [], 
        isLoading,
        error 
    } = useClientesReport(appliedFilters);

    // ✅ TRATAMENTO DE ERRO PADRONIZADO
    useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de clientes:', error);
            
            let errorMessage = 'Ocorreu um erro ao buscar o relatório de clientes.';
            
            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // 🔥 CARREGAR OPÇÕES DE FILTRO - PADRONIZADO
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin e
            // matriz_admin nunca viam essas opções de filtro. Os endpoints
            // já escopam sozinhos pelo JWT (filterByUserUnits).
            if (!user?.account?._id && !user?.franquia?._id && scope !== 'matriz') {
                console.log('⚠️ Usuário sem conta, franquia ou escopo matriz — sem opções de filtro');
                return;
            }

            setLoadingFilters(true);

            try {
                const accountId = user?.account?._id;
                const baseParams = accountId ? { account: accountId, limit: 1000 } : { limit: 1000 };
                console.log('🔄 Carregando filtros de clientes', baseParams);

                // ✅ PADRONIZADO: Usando a mesma estrutura dos outros componentes
                const requests = [
                    // Serviços
                    api.get('/servicos', { params: baseParams }),
                    // Profissionais
                    api.get('/users', { params: { ...baseParams, role: 'profissional', isActive: true } }),
                    // Unidades
                    api.get('/unidades', { params: baseParams }),
                    // Recursos
                    api.get('/recursos', { params: { ...baseParams, status: 'Ativo' } })
                ];

                console.log('📡 Fazendo requisições para filtros...');
                
                const [servicosRes, profissionaisRes, unidadesRes, recursosRes] = await Promise.all(requests);

                // ✅ PADRONIZADO: Mesma estrutura de extração de dados
                const filterData = {
                    servicos: servicosRes.data?.data || servicosRes.data || [],
                    profissionais: profissionaisRes.data?.data || profissionaisRes.data || [],
                    unidades: unidadesRes.data?.data || unidadesRes.data || [],
                    recursos: recursosRes.data?.data || recursosRes.data || []
                };

                setFilterOptions(filterData);

                console.log('✅ Filtros de clientes carregados com sucesso:', {
                    servicos: filterData.servicos.length,
                    profissionais: filterData.profissionais.length,
                    unidades: filterData.unidades.length,
                    recursos: filterData.recursos.length
                });

            } catch (error) {
                console.error('❌ Erro ao carregar filtros de clientes:', error);
                
                let errorMessage = 'Erro ao carregar opções de filtro.';
                
                if (error.message.includes('Servidor indisponível')) {
                    errorMessage = 'Servidor backend indisponível. Verifique se está rodando.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setAlert(errorMessage, 'danger');
                
                // ✅ FALLBACK: Definir arrays vazios para evitar quebra da UI
                setFilterOptions({
                    servicos: [],
                    profissionais: [],
                    unidades: [],
                    recursos: []
                });
            } finally {
                setLoadingFilters(false);
            }
        };
        
        fetchFilterOptions();
    }, [user, scope, setAlert]);

    // 🔥 COLUNAS DA TABELA
    const columns = [
        { 
            key: 'nome', 
            header: 'Nome', 
            render: (row) => row.nome || 'Nome não informado' 
        },
        { 
            key: 'telefone', 
            header: 'Telefone', 
            render: (row) => row.telefone || 'N/D' 
        },
        { 
            key: 'totalAgendamentos', 
            header: 'Total de Agendamentos', 
            render: (row) => row.totalAgendamentos || 0 
        },
        { 
            key: 'valorTotal', 
            header: 'Valor Gasto', 
            render: (row) => formatCurrency(row.valorTotal || 0, currency, locale)
        },
    ];

    // 🔥 ESTATÍSTICAS DOS CLIENTES
    const getClientStats = () => {
        if (!reportData || reportData.length === 0) return null;
        
        const totalClientes = reportData.length;
        const totalAgendamentos = reportData.reduce((sum, cliente) => sum + (cliente.totalAgendamentos || 0), 0);
        const totalFaturamento = reportData.reduce((sum, cliente) => sum + (cliente.valorTotal || 0), 0);
        const clientesComAgendamentos = reportData.filter(cliente => (cliente.totalAgendamentos || 0) > 0).length;
        const clientesSemAgendamentos = totalClientes - clientesComAgendamentos;
        
        return {
            totalClientes,
            totalAgendamentos,
            totalFaturamento,
            clientesComAgendamentos,
            clientesSemAgendamentos,
            ticketMedio: totalAgendamentos > 0 ? totalFaturamento / totalAgendamentos : 0
        };
    };

    const stats = getClientStats();

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    // ✅ KPIs DINÂMICOS
    const kpiCards = [
        { 
            title: "Total de Clientes", 
            value: (stats?.totalClientes || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: Users,
            description: "Clientes no período"
        },
        { 
            title: "Agendamentos", 
            value: (stats?.totalAgendamentos || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: Users,
            description: "Total de agendamentos"
        },
        {
            title: "Faturamento Total",
            value: formatCurrency(stats?.totalFaturamento || 0, currency, locale),
            prefix: "",
            icon: Users,
            description: "Valor total gasto"
        },
        {
            title: "Ticket Médio",
            value: formatCurrency(stats?.ticketMedio || 0, currency, locale),
            prefix: "",
            icon: Users,
            description: "Valor médio por agendamento"
        }
    ];

    return (
        <ReportPageLayout
            icon={<Users size={24} />}
            title="Relatório de Clientes"
            onApplyFilters={setAppliedFilters}
            filterOptions={filterOptions}
            enabledFilters={['date', 'servico', 'profissional', 'unidade', 'recurso', 'comAgendamento']}
            isLoading={isLoading || loadingFilters}
            initialFilters={initialFilterState}
            kpiCards={kpiCards}
        >
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!isBackendUnavailable && appliedFilters && (
                <div className="table-container">
                    {/* 🔥 ESTATÍSTICAS */}
                    {stats && (
                        <div className="content-panel mb-4">
                            <h4 className="panel-title">Resumo do Relatório</h4>
                            
                            {/* 🔥 DISTRIBUIÇÃO DE CLIENTES */}
                            <div className="row mt-3">
                                <div className="col-md-6">
                                    <div className="distribution-card">
                                        <h6>Distribuição por Agendamentos</h6>
                                        <div className="distribution-item">
                                            <span>Com agendamentos:</span>
                                            <strong>{stats.clientesComAgendamentos}</strong>
                                        </div>
                                        <div className="distribution-item">
                                            <span>Sem agendamentos:</span>
                                            <strong>{stats.clientesSemAgendamentos}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <ReportTable 
                        title={`Clientes ${appliedFilters.comAgendamento === 'nao' ? 'Sem' : 'Com'} Agendamentos`}
                        columns={columns} 
                        data={reportData} 
                        isLoading={isLoading}
                        emptyMessage={
                            appliedFilters.comAgendamento === 'nao'
                                ? 'Nenhum cliente sem agendamentos encontrado com os filtros aplicados.'
                                : 'Nenhum cliente com agendamentos encontrado com os filtros aplicados.'
                        }
                    />
                    
                    {/* 🔥 TOP CLIENTES */}
                    {reportData && reportData.length > 0 && (
                        <div className="content-panel mt-4">
                            <h4 className="panel-title">Top Clientes por Faturamento</h4>
                            <div className="row">
                                {reportData
                                    .sort((a, b) => (b.valorTotal || 0) - (a.valorTotal || 0))
                                    .slice(0, 5)
                                    .map((cliente, index) => (
                                        <div key={cliente._id || cliente.nome} className="col-md-6 col-lg-4 mb-3">
                                            <div className="top-client-card">
                                                <div className="top-client-rank">#{index + 1}</div>
                                                <div className="top-client-info">
                                                    <h6>{cliente.nome}</h6>
                                                    <p className="mb-1">
                                                        <strong>Agendamentos:</strong> {cliente.totalAgendamentos || 0}
                                                    </p>
                                                    <p className="mb-0">
                                                        <strong>Faturamento:</strong> {formatCurrency(cliente.valorTotal || 0, currency, locale)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}

                    {/* 🔥 ESTADO VAZIO MELHORADO */}
                    {!isLoading && reportData.length === 0 && (
                        <div className="empty-state text-center py-5">
                            <Users size={48} className="text-muted mb-3" />
                            <h5>Nenhum cliente encontrado</h5>
                            <p className="text-muted">
                                Não foram encontrados clientes para os filtros selecionados.
                                {appliedFilters.comAgendamento === 'nao' 
                                    ? ' Tente ajustar os critérios de busca.' 
                                    : ' Verifique se há agendamentos no período selecionado.'
                                }
                            </p>
                        </div>
                    )}
                </div>
            )}
        </ReportPageLayout>
    );
};

export default ClientesReport;